import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatExpansionPanel, MatExpansionPanelHeader } from "@angular/material/expansion";
import { MatIcon } from "@angular/material/icon";
import { AnalysisResult, Contract, DoraResponse } from '@shared-interfaces/shared-workflow';
import { allSameValue, atLeastOneValue } from "@shared-helper/helper-functions";
import { TranslateModule } from '@ngx-translate/core';
import { MarkdownComponent } from 'ngx-markdown';
import { OutputSchema, DoraFieldConfig } from '../../../administration/shared/api.service';
import { classifySummaryAnswer } from '../../classify-summary-answer';

@Component({
    selector: 'analysis-result-item',
    standalone: true,
    imports: [
        CommonModule,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatIcon,
        TranslateModule,
        MarkdownComponent
    ],
    templateUrl: './analysis-result-item.component.html',
    styleUrl: './analysis-result-item.component.scss'
})
export class AnalysisResultItemComponent {

    @Input() contract!: AnalysisResult;
    @Input() contractIndex!: number;
    @Input() outputSchema?: OutputSchema | null;

    public get colorName() {
        // New schema: use analysisSummary.shortAnswer (aggregated per-question answer)
        // so the panel color always matches the badge label.
        if (this.contract.analysisSummary?.shortAnswer) {
            return this.summaryColorName;
        }

        // Old schema fallback: derive color from individual file answers.
        const shortAnswers = this.contract.files.map(file => {
            const rawAnswer = file.result?.['short_answer'] ?? file.shortAnswer ?? '';
            return typeof rawAnswer === 'string' ? rawAnswer.toLowerCase().trim() : '';
        });

        // At least one "yes" -> green
        if (shortAnswers.some(a => a === 'yes' || a === 'ja' || a === 'erfüllt')) {
            return 'green';
        }
        // All "no" -> red
        if (shortAnswers.every(a => a === 'no' || a === 'nein' || a === 'nicht erfüllt')) {
            return 'red';
        }
        // Mix of "no" and "to be checked" -> orange
        return 'orange';
    }

    public get summaryColorName() {
        if (!this.contract.analysisSummary) return 'orange';

        // Use word-boundary based classification. Previously this used
        // String.includes() which matched substrings inside unrelated words
        // (e.g. 'no' inside "Informationen", 'nein' inside "keinen"),
        // flipping genuine "Yes" summaries to red/orange.
        switch (classifySummaryAnswer(this.contract.analysisSummary.shortAnswer)) {
            case 'yes':
                return 'green';
            case 'no':
                return 'red';
            default:
                return 'orange';
        }
    }

    public get hasSummary() {
        return this.contract.analysisSummary && 
               (this.contract.analysisSummary.summary || this.contract.analysisSummary.shortAnswer);
    }

    public get hasError(): boolean {
        return this.contract.files.some(f => f.error !== null && f.error !== undefined);
    }

    public getFieldValue(file: DoraResponse, fieldName: string): any {
        // Try new structure first (exact match)
        if (file.result && file.result[fieldName] !== undefined) {
            return file.result[fieldName];
        }
        // If camelCase field not found in result, try snake_case equivalent
        // e.g. 'shortAnswer' -> 'short_answer'
        if (file.result) {
            const snakeCase = fieldName.replace(/([A-Z])/g, '_$1').toLowerCase();
            if (file.result[snakeCase] !== undefined) {
                return file.result[snakeCase];
            }
        }
        // Fallback to legacy top-level fields
        return (file as any)[fieldName];
    }

    public get displayFields(): string[] {
        if (this.outputSchema?.fields && this.outputSchema.fields.length > 0) {
            return this.outputSchema.fields.map((f: DoraFieldConfig) => f.field_name);
        }

        // Derive columns from the actual result keys returned by the backend
        const firstResultWithData = this.contract.files.find(f => f.result && Object.keys(f.result).length > 0);
        if (firstResultWithData?.result) {
            return Object.keys(firstResultWithData.result);
        }

        // Last resort: legacy top-level fields
        return ['shortAnswer', 'summary', 'passage'];
    }

    public formatFieldLabel(fieldName: string): string {
        return fieldName
            .replace(/_/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/\b\w/g, c => c.toUpperCase());
    }

    public formatFieldValue(value: any): string {
        if (value === null || value === undefined) {
            return '';
        }
        if (typeof value === 'object') {
            return JSON.stringify(value, null, 2);
        }
        return String(value);
    }

}
